/**
 * @file     useChatShell.ts
 * @purpose  The three imperative behaviors of the chat shell, extracted from
 *           ChatWorkspace so each is independently testable and reusable:
 *           self-measuring shell height, bottom-follow with a jump pill, and
 *           the wheel edge-latch (scroll speed bump).
 */
'use client';

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

// Pre-measure fallback: viewport minus the navbar's usual footprint. Replaced
// by the real measurement on the first client frame.
const FALLBACK_HEIGHT = 'calc(100dvh - 88px)';
// The shell never collapses below this, whatever chrome or keyboard eats the
// viewport (the composer alone needs ~200px to stay usable).
const MIN_SHELL_PX = 320;
// Breathing room between the shell and the viewport bottom edge.
const BOTTOM_GUTTER_PX = 8;

/** Shell height = the space between the shell's real top edge and the visual
 *  viewport bottom. One measured number, one fallback; recomputed on viewport
 *  or keyboard resizes and on body layout shifts (a late-mounting announcement
 *  bar pushes the shell down after first paint). Measurements are skipped
 *  while the page is scrolled past the shell top: rect.top would be negative
 *  and the math would size the shell against an origin it no longer occupies. */
export function useShellHeight(shellRef: RefObject<HTMLDivElement | null>): string {
  const [measuredPx, setMeasuredPx] = useState<number | null>(null);
  useEffect(() => {
    const recompute = () => {
      const el = shellRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      if (top < 0) return; // page scrolled past the shell; keep the last size
      const viewportH = window.visualViewport?.height ?? document.documentElement.clientHeight;
      setMeasuredPx(Math.max(MIN_SHELL_PX, Math.round(viewportH - top - BOTTOM_GUTTER_PX)));
    };
    recompute();
    // Settle loop: the first measurements can land mid-collapse of the hero
    // intro (its 0.45s transition moves the shell's top) or before web fonts;
    // re-measure past the animation window so the height can't freeze stale.
    const settleTimers = [120, 350, 700, 1400].map((ms) => window.setTimeout(recompute, ms));
    // Scrolling back to the top must also refresh (recomputes are skipped
    // while scrolled past the shell, so the return trip re-syncs).
    let scrollRaf = 0;
    const onScroll = () => {
      cancelAnimationFrame(scrollRaf);
      scrollRaf = requestAnimationFrame(recompute);
    };
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.visualViewport?.addEventListener('resize', recompute);
    const ro = new ResizeObserver(recompute);
    ro.observe(document.body);
    return () => {
      settleTimers.forEach(clearTimeout);
      cancelAnimationFrame(scrollRaf);
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', onScroll);
      window.visualViewport?.removeEventListener('resize', recompute);
      ro.disconnect();
    };
  }, [shellRef]);
  return measuredPx == null ? FALLBACK_HEIGHT : `${measuredPx}px`;
}

/** Lock the document while the chat is the active mobile surface. This is the
 *  core iOS Safari keyboard fix: focusing the composer makes Safari pan the
 *  DOCUMENT to lift the input above the keyboard, which fights the shell's fixed
 *  height (and trips the rect.top<0 measurement skip) -> the "push up" jank. If
 *  the document cannot scroll, that pan no-ops. Uses the position:fixed +
 *  top:-scrollY technique (the only reliable iOS lock; plain overflow:hidden is
 *  not honored there), applied to document.body (the root, so transform-ancestor
 *  containing-block quirks don't apply). Restores the exact scroll on release.
 *  No-op off `active` and on desktop, so page scroll is untouched there. */
export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active || typeof document === 'undefined') return;
    const body = document.body;
    const scrollY = window.scrollY;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      overscrollBehavior: body.style.overscrollBehavior,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      body.style.overscrollBehavior = prev.overscrollBehavior;
      window.scrollTo(0, scrollY);
    };
  }, [active]);
}

/** Follow the newest content while the reader is at the bottom; expose the
 *  detach state for the jump pill. Streaming growth has no scroll/deps signal,
 *  so a light rAF loop keeps the pin while `streaming` is true. */
export function useAutoFollow(
  threadRef: RefObject<HTMLDivElement | null>,
  deps: { messageCount: number; thinking: boolean; streaming: boolean },
) {
  const [atBottom, setAtBottom] = useState(true);
  const atBottomRef = useRef(true);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = threadRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, [threadRef]);

  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    const onScroll = () => {
      const at = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
      atBottomRef.current = at;
      setAtBottom(at);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [threadRef]);

  useEffect(() => {
    if (atBottomRef.current) scrollToBottom();
  }, [deps.messageCount, deps.thinking, scrollToBottom]);

  useEffect(() => {
    if (!deps.streaming) return;
    let raf = 0;
    const tick = () => {
      if (atBottomRef.current) scrollToBottom(false);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [deps.streaming, scrollToBottom]);

  return { atBottom, scrollToBottom };
}

/** Wheel speed bump at the thread's bottom/top edge. Each NEW gesture at an
 *  edge is absorbed for a beat before chaining to the page; gesture boundaries
 *  are detected by wheel-event quiet time, so the bump re-arms after every
 *  pause. Inactive while the thread doesn't overflow (a fresh chat must not
 *  eat page scroll), and touch keeps native chaining. */
export function useEdgeLatch(threadRef: RefObject<HTMLDivElement | null>): void {
  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    // Armed: the NEXT gesture at an edge gets absorbed. Disarms after that
    // gesture so the following one chains (the footer stays one deliberate
    // scroll away, including for discrete wheel clicks). Re-arms on leaving
    // the edge or after a dwell.
    let armed = true;
    let blocking = false;
    let lastEdgeWheelAt = 0;
    let disarmedAt = 0;
    const GESTURE_GAP_MS = 350;
    const REARM_MS = 3000;
    const onWheel = (e: WheelEvent) => {
      if (el.scrollHeight - el.clientHeight < 8) return; // no overflow: never absorb
      const atEdgeDown = e.deltaY > 0 && el.scrollHeight - el.scrollTop - el.clientHeight < 1;
      const atEdgeUp = e.deltaY < 0 && el.scrollTop < 1;
      const now = Date.now();
      if (!atEdgeDown && !atEdgeUp) {
        armed = true;
        blocking = false;
        return;
      }
      const newGesture = now - lastEdgeWheelAt > GESTURE_GAP_MS;
      lastEdgeWheelAt = now;
      if (newGesture) {
        if (armed || now - disarmedAt > REARM_MS) {
          blocking = true;
          armed = false;
          disarmedAt = now;
        } else {
          blocking = false;
        }
      }
      if (blocking) e.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [threadRef]);
}
